/**
 * Global, feature-flagged outage banner.
 *
 * Renders `null` when the flag is off OR when backend health is OK,
 * so mounting it unconditionally in `App.js` is safe and DCE-friendly:
 * with `REACT_APP_BACKEND_OUTAGE_UI_ENABLED=false` (default), the
 * `installBackendHealthDetector()` call is a no-op, health stays at
 * "OK" forever, and this component always returns null.
 *
 * All copy comes from `outageMessages.js` — never inline HTTP error
 * text into the DOM. See FOUNDER_DECISIONS.md D-035.
 */

import React, { useEffect, useState } from "react";
import { BACKEND_OUTAGE_UI_ENABLED } from "../../lib/featureFlags";
import {
  HEALTH_OK,
  getBackendHealth,
  subscribeBackendHealth,
  installBackendHealthDetector,
} from "../../lib/backendHealth";
import { GLOBAL_OUTAGE_COPY } from "../../lib/outageMessages";

export default function BackendOutageBanner() {
  const [health, setHealth] = useState(getBackendHealth());
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!BACKEND_OUTAGE_UI_ENABLED) return undefined;
    installBackendHealthDetector();
    const unsub = subscribeBackendHealth((next) => {
      setHealth(next);
      // Any transition back to OK re-arms the banner for the next
      // outage — a user who dismissed the previous message should
      // still see a new one if we degrade again.
      if (next === HEALTH_OK) setDismissed(false);
    });
    return unsub;
  }, []);

  if (!BACKEND_OUTAGE_UI_ENABLED) return null;
  if (health === HEALTH_OK) return null;
  if (dismissed) return null;

  const copy = GLOBAL_OUTAGE_COPY[health];
  if (!copy) return null;

  const toneClass =
    copy.tone === "warn"
      ? "bg-amber-50 border-amber-300 text-amber-900"
      : "bg-sky-50 border-sky-300 text-sky-900";

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="backend-outage-banner"
      className={`w-full border-b ${toneClass} px-4 py-3 text-sm leading-snug`}
    >
      <div className="max-w-md mx-auto flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <p className="font-semibold" data-testid="backend-outage-banner-title">
            {copy.title}
          </p>
          <p className="mt-0.5 opacity-90" data-testid="backend-outage-banner-body">
            {copy.body}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          data-testid="backend-outage-banner-dismiss"
          className="shrink-0 rounded-md px-2 py-1 text-xs font-medium underline decoration-transparent hover:decoration-current"
          aria-label="Dismiss outage banner"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}
